import React, { useState, useEffect } from "react";
import { useWebData } from "../../Context/webDataContext";
import image from "../../aseets/homePageAssets/home1.svg";
import { PLAY_STORE_URL, resolvePlayStoreUrl } from "../../constants/storeLinks";

const homeHeroFallback = {
  isFeatured: true,
  heading: "Redefining Comfort with Trusted",
  halfheading: "Home Services.",
  subheading:
    "Get trusted professionals for cleaning, cooking, babysitting, and more—anytime, anywhere.",
  image: image,
  link1: PLAY_STORE_URL,
  link2: "https://apps.apple.com/app/id123456789",
  state: {
    active_users: "15K+",
    total_downloads: "10K+",
    total_rating: "4.5",
    testimonials: "200+",
  },
};

function HomeHero() {
  const { getHomePageData } = useWebData();

  const [data, setData] = useState(homeHeroFallback);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const fetchedData = getHomePageData();
    if (fetchedData && Object.keys(fetchedData).length > 0) {
      setData({
        ...homeHeroFallback,
        ...fetchedData,
        link1: resolvePlayStoreUrl(fetchedData.link1),
        state: { ...homeHeroFallback.state, ...(fetchedData.state || {}) },
      });
    }
  }, [getHomePageData]);

  if (!data.isFeatured) return null;

  return (
    <section className="bg-white relative overflow-hidden">
      {/* Absolute Positioned Dots */}
      <div className="absolute lg:top-10 lg:w-4 lg:h-4 top-2 left-2 w-2 h-2 bg-[#08875D] rounded-full z-10" />
      <div className="absolute lg:top-10 lg:w-4 lg:h-4 top-2 right-2 w-2 h-2 bg-[#08875D] rounded-full z-10" />

      {/* Hero Content */}
      <div className="flex flex-col md:flex-row items-center lg:min-h-[30rem] pt-8 lg:gap-10">
        {/* Left Section */}
        <div className="md:w-1/2 text-center md:text-left space-y-6">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 leading-snug">
            {data.heading}{" "}
            <span className="text-[#08875D] block lg:inline">
              {data.halfheading}
            </span>
          </h1>
          <p className="text-gray-600 text-base md:text-lg leading-relaxed">
            {data.subheading}
          </p>

          {/* Store Buttons with Dynamic Links */}
          <div className="lg:flex gap-4 justify-center md:justify-start mt-4 hidden">
            {data.link1 && (
              <a
                href={data.link1}
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:scale-105"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Google_Play_Store_badge_EN.svg/512px-Google_Play_Store_badge_EN.svg.png"
                  alt="Google Play Store"
                  className="h-12 object-contain"
                />
              </a>
            )}
            {data.link2 && (
              <a
                href={data.link2}
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:scale-105"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="App Store"
                  className="h-12 object-contain"
                />
              </a>
            )}
          </div>
        </div>

        {/* Right Image */}
        <div className="md:w-1/2 w-full flex justify-center relative">
          {!imageLoaded && (
            <div className="absolute inset-0 top-4 bg-green-100/30 animate-pulse rounded-full w-full max-w-full md:h-auto" />
          )}
          <img
            src={data.image || image}
            alt="Hero"
            className={`max-w-full md:max-w-md transition-opacity duration-500 ease-in-out ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
            loading="eager"
            onLoad={() => setImageLoaded(true)}
          />
        </div>
      </div>

      {/* Stats Bar */}
      <div
        className="lg:p-8 p-4 text-white rounded-2xl lg:mt-0 shadow-md"
        style={{
          background:
            "linear-gradient(180deg, #1D9D67 0%, #1E8157 35.45%, #1F6546 83.28%, #1F573E 118.49%, #204533 163%)",
        }}
      >
        <div className="grid grid-cols-2 lg:flex justify-between items-center text-center gap-6">
          <div>
            <p className="text-3xl font-bold">{data.state.active_users}</p>
            <p className="text-sm opacity-90 mt-1">Active Users</p>
          </div>
          <div>
            <p className="text-3xl font-bold">{data.state.total_downloads}</p>
            <p className="text-sm opacity-90 mt-1">Total Downloads</p>
          </div>
          <div>
            <p className="text-3xl font-bold">{data.state.total_rating}</p>
            <p className="text-sm opacity-90 mt-1">Total Rating</p>
          </div>
          <div>
            <p className="text-3xl font-bold">{data.state.testimonials}</p>
            <p className="text-sm opacity-90 mt-1">Testimonials</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HomeHero;
